import { useState, useEffect } from "react";
import CrmLayout from "@/components/feature/CrmLayout";
import { botSources as initialSources, defaultBotConfig, communicationStyles, languageOptions, botStats } from "@/mocks/botIdentityData";
import type { CommunicationStyle, BotConfig } from "@/mocks/botIdentityData";
import { fetchApi } from "@/services/apiService";

export default function BotIdentityPage() {
  const [config, setConfig] = useState<BotConfig>(defaultBotConfig);
  const [sources, setSources] = useState(initialSources);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchApi<any>('/ai-agent/settings', undefined, null)
      .then((res) => {
        const data = res?.data || res;
        if (data) {
          setConfig((prev) => ({
            ...prev,
            masterEnabled: data.masterEnabled ?? data.master_enabled ?? prev.masterEnabled,
            botName: data.botName || data.bot_name || prev.botName,
            persona: data.persona || prev.persona,
            tone: data.tone || prev.tone,
            language: data.language || prev.language,
          }));

          if (Array.isArray(data.sources) && data.sources.length > 0) {
            setSources((prev) =>
              prev.map((s) => {
                const found = data.sources.find((ds: any) => ds.id === s.id);
                return found ? { ...s, enabled: found.enabled ?? s.enabled } : s;
              })
            );
          }
        }
      })
      .catch((err) => console.error("Error loading AI agent settings:", err));
  }, []);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const toggleSource = (id: string) => {
    const source = sources.find((s) => s.id === id);
    if (source?.locked) return;
    if (!config.masterEnabled) {
      showToast("Enable the bot first to manage sources", "error");
      return;
    }
    setSources((prev) =>
      prev.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s))
    );
    showToast("Source status updated");
  };

  const handleSaveBotSettings = async () => {
    setIsSaving(true);
    try {
      await fetchApi('/ai-agent/settings', {
        method: 'PUT',
        body: JSON.stringify({
          ...config,
          sources: sources.map((s) => ({ id: s.id, enabled: s.enabled })),
        }),
      });
      showToast("Bot settings saved successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to save bot settings", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const qualifiedPct = botStats.engaged > 0 ? Math.round((botStats.qualified / botStats.engaged) * 100) : 0;
  const notInterestedPct = botStats.engaged > 0 ? Math.round((botStats.notInterested / botStats.engaged) * 100) : 0;
  const noReplyPct = botStats.engaged > 0 ? Math.round((botStats.noReply / botStats.engaged) * 100) : 0;

  return (
    <CrmLayout title="Bot Identity">
      <div className="p-5 md:p-6 space-y-5 max-w-5xl">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Bot Identity</h1>
            <p className="text-sm text-gray-500 mt-0.5">Configure how your WhatsApp bot presents itself to leads</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={config.masterEnabled}
              onChange={() => setConfig((prev) => ({ ...prev, masterEnabled: !prev.masterEnabled }))}
            />
            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:start-[4px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
            <span className={`ml-3 text-sm font-semibold whitespace-nowrap ${config.masterEnabled ? "text-indigo-600" : "text-gray-400"}`}>
              {config.masterEnabled ? "Bot Active" : "Bot Disabled"}
            </span>
          </label>
        </div>

        {/* Status Banner */}
        {config.masterEnabled ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-green-600" />
            </div>
            <p className="text-sm text-green-800">
              Bot is active. New leads from enabled sources will receive automatic WhatsApp messages before agent assignment.
            </p>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-error-warning-fill text-amber-600" />
            </div>
            <p className="text-sm text-amber-800">
              Bot is disabled. All new leads will be assigned directly using assignment rules. Enable to activate WhatsApp qualification.
            </p>
          </div>
        )}

        {/* SECTION 1: Bot Identity */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-900">Bot Identity</h2>
            <p className="text-sm text-gray-500 mt-0.5">How your bot presents itself to leads on WhatsApp</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* LEFT - Avatar & Name */}
            <div>
              <div className="flex flex-col items-center mb-6">
                <div className="w-20 h-20 rounded-full bg-indigo-100 border-4 border-indigo-200 flex items-center justify-center mb-2">
                  <i className="ri-robot-2-line text-3xl text-indigo-600" />
                </div>
                <button
                  onClick={() => showToast("Avatar change coming soon — contact admin for now")}
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium cursor-pointer whitespace-nowrap"
                >
                  Change Avatar
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                    Bot Display Name
                  </label>
                  <input
                    type="text"
                    value={config.botName}
                    onChange={(e) => setConfig((prev) => ({ ...prev, botName: e.target.value }))}
                    placeholder="e.g. Aria, SalesBot, Maya"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                  />
                  <p className="text-xs text-gray-400 mt-1">This name is used in all greeting messages</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                    Bot Persona
                  </label>
                  <input
                    type="text"
                    value={config.persona}
                    onChange={(e) => setConfig((prev) => ({ ...prev, persona: e.target.value }))}
                    placeholder="e.g. Senior Sales Consultant, Property Advisor"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                  />
                  <p className="text-xs text-gray-400 mt-1">Appears under bot name in intro message</p>
                </div>
              </div>
            </div>

            {/* RIGHT - Style & Tone */}
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  Communication Style
                </label>
                <div className="space-y-2">
                  {communicationStyles.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => setConfig((prev) => ({ ...prev, tone: style.id as CommunicationStyle }))}
                      className={`w-full p-3 rounded-lg border text-left transition-all cursor-pointer ${
                        config.tone === style.id
                          ? "border-indigo-600 bg-indigo-50/50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-900">{style.label}</span>
                        {config.tone === style.id && (
                          <i className="ri-checkbox-circle-fill text-indigo-600 text-base" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{style.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  Primary Language
                </label>
                <select
                  value={config.language}
                  onChange={(e) => setConfig((prev) => ({ ...prev, language: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                >
                  {languageOptions.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Bot replies in this language by default</p>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: Source Activation */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-900">Source Activation</h2>
            <p className="text-sm text-gray-500 mt-0.5">Choose which lead channels the bot will automatically handle</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {sources.map((src) => (
              <div
                key={src.id}
                className={`p-4 rounded-xl border transition-all ${
                  src.enabled ? "border-indigo-200 bg-indigo-50/20" : "border-gray-200 bg-gray-50/50"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-900">{src.label}</span>
                  <input
                    type="checkbox"
                    checked={src.enabled}
                    onChange={() => toggleSource(src.id)}
                    disabled={src.locked || !config.masterEnabled}
                    className="rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:opacity-50"
                  />
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{src.desc}</p>
                {src.locked && (
                  <span className="inline-block mt-2 text-[10px] font-semibold text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded">
                    Requires Setup
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 3: Performance Snapshot */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-gray-900">Bot Performance Snapshot</h2>
            <p className="text-sm text-gray-500 mt-0.5">Overview of bot interactions this month</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <p className="text-2xl font-bold text-indigo-700">{botStats.engaged.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-0.5">Engaged</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-2xl font-bold text-green-700">{botStats.qualified.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-0.5">Qualified ({qualifiedPct}%)</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-2xl font-bold text-red-600">{botStats.notInterested.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-0.5">Not Interested ({notInterestedPct}%)</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-2xl font-bold text-amber-700">{botStats.noReply.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-0.5">No Reply ({noReplyPct}%)</p>
            </div>
          </div>

          <p className="text-sm">
            <a href="/settings/workflows" className="text-indigo-600 hover:text-indigo-800 font-medium">
              View full analytics in Workflow Builder →
            </a>
          </p>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSaveBotSettings}
          disabled={isSaving}
          className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm hover:shadow flex items-center justify-center gap-2"
        >
          {isSaving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
          {isSaving ? "Saving..." : "Save Bot Settings"}
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-5 right-5 z-[100] px-4 py-3 rounded-lg shadow-sm text-sm font-medium animate-slide-in bg-green-500 text-white">
          <div className="flex items-center gap-2.5">
            <i className="ri-check-line text-base" />
            {toast.message}
          </div>
        </div>
      )}
    </CrmLayout>
  );
}